import { useEffect, useState } from "react";
import { ISerializedUIField, IWDDesignModeProps, IWidgetTemplateProps } from "../components/common/common";
import { hasValue, toBool, toJSON, toNum, toStr } from "../util";
import { IContextProvider } from "../uxp";
import { extractDataFromFields } from "../components/common/dnd";
import { IParam } from "../components/common/WidgetParamsEditor";
import { ContextDatasection, IThemeProps, PillOption } from "uxp/components";
import { cloneDeep } from 'lodash-es'
import { executeAndBind, IDataSource, IDataSourceBinding, schemaAtJsonPath } from "../components/common/data-source-picker/datasource";

export interface IContextSchemaProps extends ContextDatasection {
}

type IData = { [key: string]: any }
export interface IContextData {
    params?: IData
    datasources?: IData,
    filters?: IData,
    form?: IData,
    row?: IData
    theme?: IThemeProps,
    user?: IData,
    __schema__?: { // this is to hold schema - for pill inputs
        params?: IContextSchemaProps
        sources?: IContextSchemaProps[], // this will hold datasources and params - shows in the property panel & data source/ lmaction editor uis
        theme?: IContextSchemaProps,
        user?: IContextSchemaProps,
        fields?: {
            form?: IContextSchemaProps,
            filters?: IContextSchemaProps
        }  // this will hold filters and form fields - show only in data source / lmaction editor uis
    }
    [key: string]: any
}

export function getContext(contextData: IContextData) {
    const _contextData = cloneDeep(contextData || {}) //  Object.assign({}, contextData) /// structuredClone(contextData) || {}
    if (!_contextData?.params) _contextData.params = {}
    if (!_contextData?.datasources) _contextData.datasources = {}
    if (!_contextData?.filters) _contextData.filters = {}
    if (!_contextData?.fields) _contextData.fields = {}
    if (!_contextData?.row) _contextData.row = {}
    if (!_contextData?.user) _contextData.user = {}

    if (!_contextData?.__schema__) _contextData.__schema__ = { sources: [], fields: { form: { label: 'Input fields in the widget', fields: [] }, filters: { label: 'Filters in the widget', fields: [] } } }
    if (!_contextData.__schema__.params) _contextData.__schema__.params = { label: 'Params passed to the widget', fields: [] }
    if (!_contextData.__schema__.sources) _contextData.__schema__.sources = []
    if (!_contextData.__schema__.user) _contextData.__schema__.user = { label: 'Current User', fields: [] }
    if (!_contextData.__schema__.fields) _contextData.__schema__.fields = { form: { label: 'Input fields in the widget', fields: [] }, filters: { label: 'Filters in the widget', fields: [] } }
    if (!_contextData.__schema__.fields.form) _contextData.__schema__.fields.form = { label: 'Input fields in the widget', fields: [] }
    if (!_contextData.__schema__.fields.filters) _contextData.__schema__.fields.filters = { label: 'Filters in the widget', fields: [] }

    return _contextData
}

// Helper function to safely format field paths
export function formatFieldPath(prefix: string, key: string): string {
    return (key.indexOf(' ') >= 0) ? `${prefix}['${key}']` : `${prefix}.${key}`
}

// TODO :: need to figure out a way to handle simple arrays 
function processSchema(schema: any, prefix: string) {
    let values: { label: string, value: string }[] = []

    if (!schema) return values
    if (schema.hasOwnProperty('type') && ['array', 'object'].includes(schema.type)) {

        const _values = processSchema(schema.type == 'array' ? schema.items : schema.properties, schema.type == 'array' ? `${prefix}[0]` : prefix)
        values = values.concat(_values)
    }
    else {
        for (const key of Object.keys(schema)) {
            if (schema[key].hasOwnProperty('type') && ['array', 'object'].includes(schema[key].type)) {
                const newPrefix = formatFieldPath(prefix, key)
                const _values = processSchema(schema[key].type == 'array' ? schema[key].items : schema[key].properties, schema[key].type == 'array' ? `${newPrefix}[0]` : newPrefix)
                values = values.concat(_values)
            }
            else {
                values.push({
                    label: key,
                    value: formatFieldPath(prefix, key)
                })
            }
        }
    }
    return values
}

async function extractContextFromDatasources(designer: IWDDesignModeProps, uxpContext: IContextProvider, initialData: IContextData, datasources: IDataSource[], bindings: IDataSourceBinding[], prepend?: boolean) {
    const _contextData = { ...initialData }
    if (designer && (designer.mode == 'preview' || designer.mode == 'thumbnail')) {
        // in design mode - load schema
        for (const b of bindings) {
            if (!!b) {
                const _ds = datasources.find(d => d.id == b.source)
                if (_ds) {
                    const _schema = schemaAtJsonPath(_ds.schema, b.path)
                    const _processed = processSchema(_schema, formatFieldPath('$.datasources', _ds.id))

                    // append or prepend to context
                    const _temp = {
                        label: 'Datasource: ' + ((_ds.type == 'custom-json' || _ds.type == 'query') ? _ds.name : `${_ds.model} - ${_ds.action}`),
                        fields: _processed
                    }
                    if (prepend) (_contextData.__schema__.sources as any[]).unshift(_temp)
                    else (_contextData.__schema__.sources as any[]).push(_temp)
                }
            }
        }
    }
    else {
        // in live mode - load actual data 
        for (const b of bindings) {
            try {
                const _data = await executeAndBind(uxpContext, b, datasources, {}, _contextData)
                _contextData.datasources[b.source] = _data
            }
            catch (e) {
                console.log('PopulatingContextData: unable to get data from datasource. Binding: ', b, ' , error: ', e)
            }
        }
    }
    return _contextData
}

async function extractFieldContext(contextData: IContextData, designer: IWDDesignModeProps, props: IWidgetTemplateProps, fields: ISerializedUIField[], key?: 'form' | 'filters') {
    // Always populate schema so pill inputs work in all designer modes
    const _values: any[] = []
    for (const f of fields) {
        if (f.isInputField) {
            const basePath = formatFieldPath(`$.${key || 'form'}`, f.id)
            _values.push({ label: f.id, value: basePath })

            if (f.type === 'daterange' || f.metaType === 'daterange') {
                _values.push({ label: `${f.id} (start)`, value: `${basePath}.start` })
                _values.push({ label: `${f.id} (end)`, value: `${basePath}.end` })
            }
        }
    }
    contextData.__schema__.fields[key || 'form'].fields = _values

    if (designer && (designer.mode == 'preview' || designer.mode == 'thumbnail')) {
        contextData[key || 'form'] = {} // reset form/filter values on design mode
    }
    else {
        contextData = extractDataFromFields(props, contextData, fields, key || 'form')
    }
    return contextData
}

function extractParamValueFromProps(props: IWidgetTemplateProps, param: string, defaultValue?: any) {
    if (!!props?.params?.[param]) return props.params[param]
    if (!!(props as any)?.[param]) return (props as any)[param]
    return defaultValue || null
}

function parseSearchParams() {
    const searchParams = new URLSearchParams(window.location.search);
    let allParams: Record<string, any> = {};

    for (const [key, value] of searchParams.entries()) {
        allParams[key] = value;
    }

    const { __props__, __layout__, ...rest } = allParams;
    // TODO extract params from __props__

    return rest || {};
}

function isNullOrEmpty(value: any): boolean {
    if (value === null || value === undefined) return true;
    if (['string', 'number'].includes(typeof value)) return !hasValue(value, true, true);
    if (typeof value == 'boolean') return false
    return !value
}
function extractContextFromParams(designer: IWDDesignModeProps, contextData: IContextData, props: IWidgetTemplateProps) {
    const _contextData = { ...contextData }

    if (designer?.mode == 'preview' || designer?.mode == 'thumbnail') {
        const _params: PillOption[] = []
        for (const p of (props?.uiProps?.params || []) as IParam[]) {
            _params.push({
                label: p.name,
                value: formatFieldPath('$.params', p.name as string),
                formatters: hasValue(p?.type) ? p.type == 'text' ? ['string'] : [p.type] : []
            })
        }
        _contextData.__schema__.params.fields = _params
    }
    else {
        const _params: IData = {}

        const qsParams = parseSearchParams();
        for (const p of (props?.uiProps?.params || []) as IParam[]) {
            let _value = extractParamValueFromProps(props, p.name)

            console.log('gettingurlparams_value1', _value)

            // if _value is empty try to get from url
            if (isNullOrEmpty(_value)) {
                _value = qsParams?.[p.name];
                console.log('gettingurlparams_value2', _value, qsParams, p)

            }
            if (isNullOrEmpty(_value)) {
                _value = p.defaultValue;
            }
            console.log('gettingurlparams_value3', _value)

            const _type = typeof _value
            if (_type !== p.type) {
                switch (p.type) {
                    case 'number':
                        _value = toNum(_value)
                        break;
                    case 'json':
                        _value = toJSON(_value)
                        break;
                    case 'boolean':
                        _value = toBool(_value)
                        break;
                    default:
                        _value = toStr(_value)
                }
            }
            _params[p.name] = _value
        }
        _contextData.params = _params
    }
    return _contextData
}

function extractContextFromTheme(designer: IWDDesignModeProps, contextData: IContextData, theme: IThemeProps): IContextData {
    const _contextData = { ...contextData }
    if (designer?.mode == "preview" || designer?.mode == "thumbnail") {
        _contextData.__schema__.theme = {
            label: "Theme",
            fields: Object.keys(theme).map(t => ({
                label: t,
                value: formatFieldPath('$.theme', t)
            }))
        }
    } else {
        _contextData.theme = theme
    }
    return _contextData
}

async function extractContextFromUser(designer: IWDDesignModeProps, contextData: IContextData, uxpContext: IContextProvider): Promise<IContextData> {
    const _contextData = { ...contextData }
    const userFields = ['key', 'name', 'email', 'userGroup']

    if (designer?.mode === 'preview' || designer?.mode === 'thumbnail') {
        _contextData.__schema__.user = {
            label: 'Current User',
            fields: userFields.map(f => ({ label: f, value: formatFieldPath('$.user', f) }))
        }
    } else {
        try {
            const details = await uxpContext.getUserDetails()
            _contextData.user = { ...details, key: details.key || uxpContext.userKey }
        } catch (e) {
            console.log('PopulatingContextData: unable to get user details', e)
        }
    }
    return _contextData
}

async function loadInitialData(props: IWidgetTemplateProps) {
    let contextData: IContextData = getContext({})
    // first get params
    contextData = extractContextFromParams(props?.designer, contextData, props)
    contextData = extractContextFromTheme(props.designer, contextData, props.uxpContext.theme)
    contextData = await extractContextFromUser(props.designer, contextData, props.uxpContext)

    // process datasources 
    const datasources = (props?.uiProps?.datasources || []) as IDataSource[]
    const bindings = (props?.uiProps?.initialSources || []) as IDataSourceBinding[]
    contextData = await extractContextFromDatasources(props.designer, props.uxpContext, contextData, datasources, bindings)

    // get filters and form data. Some legacy templates store a non-array under
    // uiProps.fields (e.g. gridlayout's { boxProps: [...] }) — v4 crashed the
    // async chain on those (extraction silently never ran); skip them instead.
    if (Array.isArray(props?.uiProps?.fields)) {
        // get form data
        let fields: ISerializedUIField[] = props?.uiProps?.fields || []
        contextData = await extractFieldContext(contextData, props.designer, props, fields)
    }

    if (Array.isArray(props?.uiProps?.filter?.fields)) {
        // get filter data
        let filterFields: ISerializedUIField[] = props?.uiProps?.filter?.fields || []
        contextData = await extractFieldContext(contextData, props.designer, props, filterFields, 'filters')
    }

    return contextData
}

export function appendDataToContextBySource(contextData: IContextData, source: IDataSourceBinding, data: any) {
    const _contextData = getContext(contextData)
    if (!source || !source?.source) return _contextData
    _contextData.datasources[source.source] = data
    return _contextData
}

export async function appendWidgetDatasources(contextData: IContextData, props: IWidgetTemplateProps, widgetDataSources: IDataSourceBinding[]) {
    if (!widgetDataSources || widgetDataSources?.length == 0) return contextData

    const datasources = (props?.uiProps?.datasources || []) as IDataSource[]
    contextData = await extractContextFromDatasources(props.designer, props.uxpContext, contextData, datasources, widgetDataSources, true)
    return contextData
}

export function appendRowToContextData(contextData: IContextData, props: IWidgetTemplateProps, binding: IDataSourceBinding) {
    const _contextData = getContext(contextData)
    if (!binding) return _contextData
    const datasources = (props?.uiProps?.datasources || []) as IDataSource[]
    const ds = datasources.find(d => d.id === binding.source)
    if (!ds) return _contextData

    const _schema = schemaAtJsonPath(ds.schema, binding.path)
    const processed = processSchema(_schema?.items?.properties, '$.row')

    _contextData.__schema__.sources.unshift({
        label: 'From each item in datasource : ' + ((ds.type == 'custom-json' || ds.type == 'query') ? ds.name : `${ds.model} - ${ds.action}`),
        fields: processed
    })

    return _contextData
}

export const useInitialContextData = (props: IWidgetTemplateProps, widgetDataSources?: IDataSourceBinding[]) => {

    const [isLoading, setIsLoading] = useState<boolean>(true)
    const [initialContextData, setInitialContextData] = useState<IContextData>({})
    const [currentUrl, setCurrentUrl] = useState<string>(window.location.href)

    const individualParams = props?.uiProps?.params?.reduce((a:any, p:any)=> {a[p.name] = props[p.name]; return a; }, {} as any)
    let dependencies: any = [
        props?.contextData,
        JSON.stringify(props?.params),
        JSON.stringify(individualParams),
        props?.uxpContext?.theme,
        JSON.stringify(props?.uiProps?.filter?.fields),
        // loadInitialData also reads datasources, initialSources bindings and the
        // form `fields` slice. Without these keys, editing datasources/bindings/
        // fields in the designer left stale data/schema in the canvas. Stringified
        // so referentially-new-but-deep-equal uiProps do NOT refire the effect.
        JSON.stringify(props?.uiProps?.datasources),
        JSON.stringify(props?.uiProps?.initialSources),
        JSON.stringify(props?.uiProps?.fields),
        props?.designer?.mode,
        currentUrl
    ];

    useEffect(() => {
        const handleUrlChange = () => {
            setCurrentUrl(window.location.href)
        }

        // Listen for popstate (back/forward buttons)
        window.addEventListener('popstate', handleUrlChange)

        return () => {
            window.removeEventListener('popstate', handleUrlChange)
        }
    }, [])

    useEffect(() => {
        extractInitialData()
    }, dependencies);

    async function extractInitialData() {
        setIsLoading(true)

        // load initial data (initial source , params, fields, filters)
        let contextData = await loadInitialData(props)

        // load widget datasources 
        contextData = await appendWidgetDatasources(contextData, props, widgetDataSources);
        console.log('initial_context_data_extracted', contextData)
        setInitialContextData(contextData)
        setIsLoading(false)
    }
    return { isLoading, initialContextData }
}